/** Shared types + utilities for renderer modules.
 *
 * Lives outside `./index.ts` so individual renderer files
 * (JsonPreviewRenderer, MapInlineRenderer, etc.) can import these
 * without forming a cycle: `index.ts` references the renderer modules
 * for its dispatch map, and those modules reference these shared
 * primitives. If JsonPreviewRenderer imported from `./index` AND
 * `index` imported MapInlineRenderer (which transitively imports
 * JsonPreviewRenderer), the dispatch-map initializer would hit a
 * TDZ error on `MapInlineRenderer`.
 */
import React from 'react';

export interface RendererProps {
  toolCall: any;
  darkMode: boolean;
}

// Normalize tool name across admin-chat (tool_name) and persona-chat (identity.tool_name) shapes.
export function getToolName(toolCall: any): string {
  return toolCall?.identity?.tool_name ?? toolCall?.tool_name ?? 'unknown';
}

// Extract the tool's result payload:
//   - admin-chat: result is a JSON-stringified `{success, result, ...}` envelope
//   - persona-chat: result is already a parsed object/scalar
// Unwraps the `{result}` envelope when present.
export function getResultData(toolCall: any): any {
  const raw = toolCall?.result;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed?.result ?? parsed;
    } catch {
      return raw;
    }
  }
  if (raw && typeof raw === 'object' && 'result' in raw && 'success' in raw) {
    return raw.result;
  }
  return raw;
}

// Suppress unused-import warning for React when this file is consumed
// only for types — keeps tsconfig isolatedModules happy.
export type ReactType = React.FC<RendererProps>;
